import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  listRegistry,
  listSectors,
  listStandards,
  readCompetenceDraft,
  readMyAuditorRecord,
  saveCompetenceDraft,
  submitCompetenceRecord,
  validateCompetenceRecord,
  type AuditorRecord,
  type CompetenceRecord,
  type CompetenceValidation,
  type Sector,
} from "../api/client";
import { StepRail } from "../components/StepRail";
import { Advisories } from "./Advisories";
import { RecordSubmitted } from "./RecordSubmitted";
import { CompetenceStep } from "./steps/CompetenceStep";
import { DeclarationsStep } from "./steps/DeclarationsStep";
import { ExperienceStep } from "./steps/ExperienceStep";
import { IdentityStep } from "./steps/IdentityStep";
import { QualificationsStep } from "./steps/QualificationsStep";
import { SignStep } from "./steps/SignStep";
import {
  addRow,
  blockingCount,
  blockingFor,
  EMPTY_RECORD,
  RECORD_STEPS,
  removeRow,
  resultsFor,
  setClaim,
  setClaimSectors,
  setRow,
  stepComplete,
  toggleClaim,
  toggleClaimSector,
} from "./record";
import { idempotencyKey as issueIdempotencyKey } from "../api/idempotency";

/**
 * Auditor onboarding — the competence record, screens 12 to 18.
 *
 * Built the same way the certification body's application is, and for the same three
 * reasons:
 *
 * **The server owns whether a step is complete.** It is asked on every change and again
 * with the payload at submission, because requiredness only the browser enforces is
 * requiredness a browser can skip.
 *
 * **Blocking findings are held back until the auditor tries to leave the step.** Advisories
 * appear as soon as the answer that causes them is given, because they are consequences
 * rather than corrections — "below lead auditor, so you cannot be the signing auditor" is
 * something to know now, not an error to fix.
 *
 * **What has been typed is saved separately from what has been claimed.** The draft is its
 * own row; the competence tables are the record, and the auditor register reads those.
 *
 * Addressed by `membershipId`, not the auditor's own id — an auditor may hold more than one
 * certification-body relationship (multiple-CBs-per-auditor), each its own record, and this
 * is which one is being completed. `GET .../me` is one row per membership; this component
 * reads the one that matches.
 */
export function AuditorOnboarding({ membershipId }: { membershipId: string }) {
  const [record, setRecord] = useState<CompetenceRecord>(EMPTY_RECORD);
  const [validation, setValidation] = useState<CompetenceValidation | null>(null);
  const [step, setStep] = useState(0);
  const [furthest, setFurthest] = useState(0);
  const [attempted, setAttempted] = useState<ReadonlySet<number>>(new Set());
  const [loaded, setLoaded] = useState(false);

  const [me, setMe] = useState<AuditorRecord | null>(null);
  const [timezones, setTimezones] = useState<string[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [standards, setStandards] = useState<string[]>([]);

  const [submitted, setSubmitted] = useState<AuditorRecord | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitFailure, setSubmitFailure] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  /** One key per attempt at *this* record, minted once — see the CB wizard. */
  const idempotencyKey = useRef(issueIdempotencyKey());

  useEffect(() => {
    Promise.all([
      readMyAuditorRecord(),
      readCompetenceDraft(membershipId),
      listRegistry("timezones"),
      listSectors(),
      listStandards(),
    ])
      .then(([mine, saved, zones, sectorList, standardList]) => {
        const record = mine.find((entry) => entry.membership_id === membershipId) ?? null;
        setMe(record);
        setTimezones(zones);
        setSectors(sectorList);
        setStandards(standardList);
        // A submitted record is not a form. Screen 18 is what it looks like. Status,
        // not `submitted_at` — that field is shared across every certification-body
        // relationship now, and `invited` is this membership's own not-yet-completed.
        if (record && record.status !== "invited") {
          setSubmitted(record);
          return;
        }
        if (saved) setRecord({ ...EMPTY_RECORD, ...saved });
      })
      .catch((cause: unknown) => {
        setFailure(cause instanceof ApiError ? cause.message : "Your record could not be read.");
      })
      .finally(() => setLoaded(true));
  }, [membershipId]);

  // One validation in flight at a time, and a later record always wins — out-of-order
  // responses would show findings against text that has already been corrected.
  const generation = useRef(0);
  useEffect(() => {
    if (!loaded || submitted) return;
    const mine = ++generation.current;
    const timer = window.setTimeout(() => {
      validateCompetenceRecord(membershipId, record)
        .then((result) => {
          if (generation.current === mine) setValidation(result);
        })
        .catch((cause: unknown) => {
          setFailure(cause instanceof ApiError ? cause.message : "Validation unavailable.");
        });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [record, membershipId, loaded, submitted]);

  // Autosaved on the same rhythm, and quietly: losing a draft is an inconvenience, and a
  // red banner over a form someone is still typing into is worse.
  useEffect(() => {
    if (!loaded || submitted) return;
    const timer = window.setTimeout(() => {
      void saveCompetenceDraft(membershipId, record).catch(() => undefined);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [record, membershipId, loaded, submitted]);

  const definition = RECORD_STEPS[step];
  const showBlocking = attempted.has(step);

  const set = useCallback((patch: Partial<CompetenceRecord>) => {
    setRecord((current) => ({ ...current, ...patch }));
  }, []);

  const errorFor = useCallback(
    (field: string) => (showBlocking ? blockingFor(validation, definition, field) : undefined),
    [showBlocking, validation, definition],
  );

  const canContinue = stepComplete(validation, definition);
  const outstanding = blockingCount(validation, definition);
  const isLast = step === RECORD_STEPS.length - 1;

  const advance = () => {
    setAttempted((current) => new Set(current).add(step));
    if (!canContinue) return;
    const next = Math.min(step + 1, RECORD_STEPS.length - 1);
    setStep(next);
    setFurthest((current) => Math.max(current, next));
  };

  const submit = async () => {
    setAttempted((current) => new Set(current).add(step));
    if (validation?.complete !== true) return;
    setSubmitting(true);
    setSubmitFailure(null);
    try {
      setSubmitted(await submitCompetenceRecord(membershipId, record, idempotencyKey.current));
    } catch (cause) {
      setSubmitFailure(
        cause instanceof ApiError ? cause.message : "The record could not be submitted.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <RecordSubmitted record={submitted} />;

  const footnote = () => {
    if (showBlocking && outstanding > 0) {
      return `Complete the required fields on this step to continue — ${outstanding} outstanding.`;
    }
    if (!canContinue) return "Complete the required fields on this step to continue.";
    if (!isLast) return "This step is complete.";
    if (validation?.complete !== true) {
      return "An earlier step is still incomplete. The record is submitted as one payload.";
    }
    return null;
  };

  return (
    <>
      <div className="between">
        <div>
          <div className="eyebrow">
            Auditor onboarding
            {me?.tenant_name ? ` · invited by ${me.tenant_name}` : ""}
          </div>
          <h1>{definition?.title}</h1>
        </div>
      </div>

      <StepRail
        titles={RECORD_STEPS.map((entry) => entry.title)}
        current={step}
        furthest={furthest}
        isComplete={(index) => stepComplete(validation, RECORD_STEPS[index])}
        onSelect={setStep}
      />

      {failure ? (
        <p className="problem" role="alert">
          {failure}
        </p>
      ) : null}

      {step === 0 ? (
        <IdentityStep
          record={record}
          set={set}
          timezones={timezones}
          invitedBy={me?.tenant_name ?? null}
          proposedStandards={me?.proposed_standards ?? []}
          errorFor={errorFor}
        />
      ) : null}
      {/*
        Competency — screens 13 and 14, one stop. The evidence of certification and what it
        is held for are two halves of the same claim (Cl. 7.1.2 reads them together), and
        asking for them on consecutive screens made a reader answer "ISO 9001" twice.

        Two cards rather than one, because the record still has two shapes in it: a list of
        certificates, and a claim per standard by sector. Merging the *cards* would produce
        a form neither the data dictionary nor the validator recognises.
      */}
      {step === 1 ? (
        <>
          <QualificationsStep
            record={record}
            set={set}
            onAdd={() => setRecord((current) => addRow(current, "certifications"))}
            onSet={(index, patch) =>
              setRecord((current) => setRow(current, "certifications", index, patch))
            }
            onRemove={(index) =>
              setRecord((current) => removeRow(current, "certifications", index))
            }
            errorFor={errorFor}
          />
          <CompetenceStep
            record={record}
            standards={standards}
            sectors={sectors}
            proposedStandards={me?.proposed_standards ?? []}
            onToggle={(standard) => setRecord((current) => toggleClaim(current, standard))}
            onSet={(standard, patch) =>
              setRecord((current) => setClaim(current, standard, patch))
            }
            onToggleSector={(standard, sector) =>
              setRecord((current) => toggleClaimSector(current, standard, sector))
            }
            onSetSectors={(standard, chosen) =>
              setRecord((current) => setClaimSectors(current, standard, chosen))
            }
            errorFor={errorFor}
          />
        </>
      ) : null}
      {step === 2 ? (
        <ExperienceStep
          record={record}
          set={set}
          onAdd={() => setRecord((current) => addRow(current, "training"))}
          onSet={(index, patch) =>
            setRecord((current) => setRow(current, "training", index, patch))
          }
          onRemove={(index) => setRecord((current) => removeRow(current, "training", index))}
          errorFor={errorFor}
        />
      ) : null}
      {step === 3 ? (
        <DeclarationsStep
          record={record}
          set={set}
          onAdd={() => setRecord((current) => addRow(current, "exclusions"))}
          onSet={(index, patch) =>
            setRecord((current) => setRow(current, "exclusions", index, patch))
          }
          onRemove={(index) => setRecord((current) => removeRow(current, "exclusions", index))}
          errorFor={errorFor}
        />
      ) : null}
      {step === 4 ? <SignStep record={record} set={set} errorFor={errorFor} /> : null}

      <Advisories results={resultsFor(validation, definition)} />

      {submitFailure ? (
        <p className="problem" role="alert">
          {submitFailure}
        </p>
      ) : null}

      <div className="row" style={{ justifyContent: "space-between" }}>
        <button
          type="button"
          className="btn sec"
          disabled={step === 0}
          onClick={() => setStep((current) => Math.max(0, current - 1))}
        >
          Back
        </button>
        {isLast ? (
          <button type="button" className="btn" disabled={submitting} onClick={() => void submit()}>
            {submitting ? "Submitting…" : "Sign and submit for verification"}
          </button>
        ) : (
          <button type="button" className="btn" onClick={advance}>
            Continue
          </button>
        )}
      </div>

      {footnote() ? <p className="foot">{footnote()}</p> : null}
    </>
  );
}
